// Inventory unit vocabulary + conversions, shared between the Inventory
// dashboard and the inline add-stock-item modal reused by the product form.
// Extracted verbatim from StoreInventoryDashboard so both screens stay in sync.

export const UNIT_OPTIONS = {
  weight: [
    { value: 'kg', label: 'Kilograms (kg)' },
    { value: 'g', label: 'Grams (g)' },
    { value: 'lb', label: 'Pounds (lb)' },
    { value: 'oz', label: 'Ounces (oz)' },
    { value: 'liter', label: 'Liters (L)' },
    { value: 'ml', label: 'Milliliters (ml)' },
  ],
  quantity: [
    { value: 'each', label: 'Each / Piece' },
    { value: 'pack', label: 'Pack' },
    { value: 'box', label: 'Box' },
    { value: 'bag', label: 'Bag' },
    { value: 'bottle', label: 'Bottle' },
    { value: 'can', label: 'Can' },
    { value: 'roll', label: 'Roll' },
    { value: 'dozen', label: 'Dozen' },
    { value: 'pair', label: 'Pair' },
    { value: 'set', label: 'Set' },
  ],
} as const

export type UnitMode = 'weight' | 'quantity'

export const CONVERSION_FACTORS: Record<string, Record<string, number>> = {
  g:     { kg: 0.001, oz: 0.035274, lb: 0.00220462 },
  kg:    { g: 1000, oz: 35.274, lb: 2.20462 },
  oz:    { g: 28.3495, kg: 0.0283495, lb: 0.0625 },
  lb:    { g: 453.592, kg: 0.453592, oz: 16 },
  ml:    { liter: 0.001 },
  liter: { ml: 1000 },
  // Count units. Lets a recipe deduct 1 'each' from stock kept by the 'dozen'
  // (1 each = 1/12 dozen), etc. — the egg-by-the-dozen case.
  each:  { dozen: 1 / 12, pair: 0.5 },
  dozen: { each: 12, pair: 6 },
  pair:  { each: 2, dozen: 1 / 6 },
}

/**
 * Spellings of a canonical unit that arrive from imports and hand-typed data
 * (F-FE-N165). NinetyPlus's menu import wrote `pc` for cups and straws, which
 * matched nothing here: the recipe editor could neither convert it nor show it,
 * and its select silently displayed "Each / Piece" over the stored `pc`.
 * Mirrors unitAliases in backend/internal/handlers/stock_units.go.
 */
export const UNIT_ALIASES: Record<string, string> = {
  pc: 'each', pcs: 'each', piece: 'each', pieces: 'each', ea: 'each', no: 'each', nos: 'each',
  unit: 'each', units: 'each',
  gm: 'g', gms: 'g', gram: 'g', grams: 'g',
  kgs: 'kg', kilogram: 'kg', kilograms: 'kg',
  l: 'liter', ltr: 'liter', litre: 'liter', litres: 'liter', liters: 'liter',
  millilitre: 'ml', milliliter: 'ml',
}

/** The canonical spelling of a unit — lower-cased, trimmed, aliases folded. */
export function canonicalUnit(unit: string | null | undefined): string {
  const u = (unit ?? '').trim().toLowerCase()
  return UNIT_ALIASES[u] ?? u
}

export function convertUnits(qty: number, fromUnitRaw: string, toUnitRaw: string): number | null {
  const fromUnit = canonicalUnit(fromUnitRaw)
  const toUnit = canonicalUnit(toUnitRaw)
  if (fromUnit === toUnit) return qty
  const factor = CONVERSION_FACTORS[fromUnit]?.[toUnit]
  if (factor == null) return null
  return qty * factor
}

const WEIGHT_UNITS = ['kg', 'g', 'lb', 'oz']
const VOLUME_UNITS = ['liter', 'ml']
const COUNT_UNITS = ['each', 'dozen', 'pair']

// getCompatibleUnits returns the units a quantity in `unit` can be expressed in
// (and converted between). Units outside the known families (pack, box, …) only
// convert to themselves.
export function getCompatibleUnits(unitRaw: string): string[] {
  const unit = canonicalUnit(unitRaw)
  if (WEIGHT_UNITS.includes(unit)) return WEIGHT_UNITS
  if (VOLUME_UNITS.includes(unit)) return VOLUME_UNITS
  if (COUNT_UNITS.includes(unit)) return COUNT_UNITS
  return [unit]
}

// allUnitOptions flattens the unit vocabulary for pickers.
export const allUnitOptions: { value: string; label: string }[] = [
  ...UNIT_OPTIONS.weight,
  ...UNIT_OPTIONS.quantity,
]
